<template>
	<div class="app-avatar-editor">
		<div
			v-if="!imageSrc"
			class="app-avatar-editor__upload-section"
			@click="triggerFileInput"
		>
			<div class="app-avatar-editor__upload-section-logo">
				<app-icon
					class="app-avatar-editor__upload-section-logo-icon"
					name="photo-add"
					width="30px"
					color="#979797"
					hover-color="#3F884F"
				/>
			</div>
			<div class="app-avatar-editor__upload-section-title">
				{{ $t("ui_choose_file") }}
			</div>
			<input
				ref="$fileInput"
				type="file"
				accept="image/*"
				@change="handleFileUpload"
				:style="{ display: 'none' }"
			/>
		</div>

		<div
			v-else
			class="app-avatar-editor__editor-container"
		>
			<app-loader v-if="isLoading" :style="loaderStyle"/>
			<div class="app-avatar-editor__editor-container-preview-bar">
				<div class="app-avatar-editor__editor-container-preview-bar-circle">
					<img :src="previewImage" v-if="previewImage" alt="Preview" />
				</div>
				<div class="app-avatar-editor__editor-container-preview-bar-file-name">
					{{ fileName }}
				</div>
				<app-icon
					class="app-avatar-editor__editor-container-preview-bar-icon"
					name="delete-outline"
					width="18px"
					color="#979797"
					hover-color="#BA4C4C"
					cursor="pointer"
					@click="dropImg"
				/>
			</div>

			<div class="app-avatar-editor__editor-container-toolbar">
				<div class="app-avatar-editor__editor-container-toolbar-block">
					<app-tooltip :text="$t('ui_flip_horizontal')">
						<template #activator>
							<app-icon
								name="flip-vertical"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="flipHorizontal"
							/>
						</template>
					</app-tooltip>
					<app-tooltip :text="$t('ui_flip_vertical')">
						<template #activator>
							<app-icon
								name="flip-horizontal"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="flipVertical"
							/>
						</template>
					</app-tooltip>
				</div>
				<div class="app-avatar-editor__editor-container-toolbar-block">
					<app-tooltip :text="$t('ui_rotate_left_90')">
						<template #activator>
							<app-icon
								name="rotate-left"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="rotateLeft"
							/>
						</template>
					</app-tooltip>
					<app-tooltip :text="$t('ui_rotate_right_90')">
						<template #activator>
							<app-icon
								name="rotate-right"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="rotateRight"
							/>
						</template>
					</app-tooltip>
					<div class="app-avatar-editor__editor-container-toolbar-block-rotate">
						<app-tooltip :text="$t('ui_rotate_degrees')">
							<template #activator>
								<app-icon
									name="park-outline_rotation"
									width="15px"
									height="18px"
									color="#979797"
									hover-color="#3F884F"
									cursor="pointer"
									@click="editHandleDeg"
								/>
							</template>
						</app-tooltip>
						<div
							v-if="!isHandleDegValueEdited"
							class="app-avatar-editor__editor-container-toolbar-block-rotate-input"
							@click="editHandleDeg"
						>
							{{ handleDegValue }}
						</div>
						<file-input
							v-else
							ref="$inputDegRef"
							v-model="handleDegValue"
							@enter="rotateByDegrees(parseInt($event))"
						/>
					</div>
				</div>
				<div
					class="app-avatar-editor__editor-container-toolbar-block"
					:style="{ flex: 1 }"
				>
					<app-tooltip :text="$t('ui_zoom_in')">
						<template #activator>
							<app-icon
								name="zoom-in"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="zoomIn"
							/>
						</template>
					</app-tooltip>
					<app-tooltip :text="$t('ui_zoom_out')">
						<template #activator>
							<app-icon
								name="zoom-out"
								width="20px"
								color="#979797"
								hover-color="#3F884F"
								cursor="pointer"
								@click="zoomOut"
							/>
						</template>
					</app-tooltip>
				</div>
				<span
					class="app-avatar-editor__editor-container-toolbar-drop"
					@click="resetAll"
				>
					{{ $t("selector_reset") }}
				</span>
			</div>

			<div class="app-avatar-editor__editor-container-cropper-area">
				<cropper
					ref="$cropperRef"
					class="app-avatar-editor__editor-container-cropper"
					:src="imageSrc"
					:stencil-props="{
						aspectRatio: 1
					}"
					:resize-image="{
						wheel: true,
						touch: true,
						adjustStencil: false
					}"
					@change="handleCropperChange"
					@ready="handleCropperReady"
				/>
			</div>

			<div class="app-avatar-editor__editor-container-save-section">
				<app-button
					:label="$t('save')"
					@click="saveImage"
					:disabled="isLoading"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	nextTick
} from 'vue';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import FileInput from "@/components/file/FileInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import {FileApi} from "@/ api/FileApi";

type FlipState = {
	horizontal: number;
	vertical: number;
}

const emit = defineEmits<{
	(e: 'save', imgUrl: string): void;
	(e: 'error', error: Error): void;
}>();

const isLoading = ref(false);
const $inputDegRef = ref<InstanceType<typeof FileInput> | null>(null);
const $fileInput = ref<HTMLInputElement | null>(null);
const $cropperRef = ref<InstanceType<typeof Cropper> | null>(null);
const imageSrc = ref<string | null>(null);
const originalImageSrc = ref<string | null>(null);
const fileName = ref<string>('');
const currentPreview = ref<string | null>(null);
const flipState = ref<FlipState>({ horizontal: 1, vertical: 1 });
const isCropperReady = ref<boolean>(false);
const handleDegValue = ref(0)
const isHandleDegValueEdited = ref(false)

const previewImage = computed<string | null>(() => {
	return currentPreview.value || imageSrc.value;
});

const triggerFileInput = (): void => {
	$fileInput.value?.click();
};

const handleFileUpload = (event: Event): void => {
	const input = event.target as HTMLInputElement;
	const file = input.files?.[0];
	if (!file) return;

	fileName.value = file.name;

	const reader = new FileReader();
	reader.onload = (e: ProgressEvent<FileReader>) => {
		const src = e.target?.result as string;
		imageSrc.value = src;
		originalImageSrc.value = src;
		flipState.value = { horizontal: 1, vertical: 1 };
		isCropperReady.value = false;
	};
	reader.readAsDataURL(file);

	input.value = '';
};

// Обработчик готовности кроппера
const handleCropperReady = (): void => {
	isCropperReady.value = true;
};

// Обработчик изменений в кроппере
const handleCropperChange = ({ canvas }: { canvas: HTMLCanvasElement | null }): void => {
	if (canvas) {
		currentPreview.value = canvas.toDataURL();
	}
};

// Поворот влево
const rotateLeft = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(-90);
	}
};

// Поворот вправо
const rotateRight = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(90);
	}
};

// Поворот изображения на указанное количество градусов по часовой стрелке
const rotateByDegrees = (degrees: string): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.rotate(parseInt(degrees))
	}
};

// Отражение по горизонтали
const flipHorizontal = (): void => {
	flipState.value.horizontal *= -1;
	refreshImage();
};

// Отражение по вертикали
const flipVertical = (): void => {
	flipState.value.vertical *= -1;
	refreshImage();
};

// Обновление изображения с учетом отражения
const refreshImage = (): void => {
	if (!$cropperRef.value || !originalImageSrc.value) return;

	isCropperReady.value = false;

	const img = new Image();
	img.crossOrigin = 'anonymous';
	img.onload = () => {
		const canvas = document.createElement('canvas');
		canvas.width = img.width;
		canvas.height = img.height;

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		ctx.translate(
			flipState.value.horizontal === 1 ? 0 : canvas.width,
			flipState.value.vertical === 1 ? 0 : canvas.height
		);
		ctx.scale(flipState.value.horizontal, flipState.value.vertical);
		ctx.drawImage(img, 0, 0);

		imageSrc.value = canvas.toDataURL('image/png');
	};

	img.src = originalImageSrc.value;
};

// Увеличенеи зума
const zoomIn = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.zoom(1.2);
	}
};

// Уменьшение зума
const zoomOut = (): void => {
	if ($cropperRef.value && isCropperReady.value) {
		$cropperRef.value.zoom(0.8);
	}
};

// Сброс всех изменений
const resetAll = (): void => {
	if ($cropperRef.value && originalImageSrc.value && isCropperReady.value) {
		imageSrc.value = originalImageSrc.value;
		flipState.value = { horizontal: 1, vertical: 1 };
		$cropperRef.value.reset();
		handleDegValue.value = 0
		isHandleDegValueEdited.value = false
	}
};

// Удаление картинки
const dropImg = () => {
	imageSrc.value = null
	currentPreview.value = null
}

// Сохранение изображения в виде Blob
const saveImage = async (): Promise<void> => {
	if (!$cropperRef.value || !isCropperReady.value) {
		emit('error', new Error('Ошибка инициализации обработчика аватаров'));
		return;
	}

	try {
		isLoading.value = true;
		const { canvas } = $cropperRef.value.getResult();

		if (!canvas) {
			emit('error', new Error('Ошибка доступа к canvas'));
			return;
		}

		const blob = await new Promise<Blob | null>((resolve) => {
			canvas.toBlob(resolve, 'image/png');
		});

		if (!blob) {
			emit('error', new Error('Ошибка при формировании изображения'));
			return;
		}

		const formData = new FormData();
		formData.append('File', blob);
		formData.append('Filename', fileName.value);
		formData.append('Type', '0');
		const result = await FileApi.PhotoUpload(formData);
		emit('save', result);

	} catch (error) {
		emit(
			'error',
			error instanceof Error
				? error
				: new Error('Что-то пошло не так...')
		);
	} finally {
		isLoading.value = false;
	}
};

const editHandleDeg = () => {
	isHandleDegValueEdited.value = true;
	nextTick(() => $inputDegRef.value.focus())
}

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))
</script>

<style lang="scss" scoped>
.app-avatar-editor {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	width: 100%;
	padding: 12px 8px;

	&__upload-section {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 4px 8px;
		border: 1px solid #D2D2D2;
		border-radius: 8px;
		cursor: pointer;

		&:hover {
			border-color: #3F884F;

			.app-avatar-editor__upload-section-logo {
				border-color: #3F884F;

				&-icon {
					color: #3F884F !important;
				}
			}
			.app-avatar-editor__upload-section-title {
				color: #3F884F;
			}
		}

		&-logo {
			display: flex;
			align-items: center;
			justify-content: center;
			height: 54px;
			width: 54px;
			border: 1px solid #979797;
			border-radius: 100%;
			background-color: #F2F2F2;
			box-shadow: 1px 2px 4px 0 #3B3B3B40;
		}

		&-title {
			font-size: 14px;
			line-height: 18px;
			font-weight: 500;
			color: #979797;
		}
	}

	&__editor-container {
		display: flex;
		flex-direction: column;
		position: relative;
		max-height: 100%;
		width: 100%;

		&-preview-bar {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 4px 8px;
			border: 1px solid #3F884F;
			border-radius: 8px;
			margin-bottom: 12px;

			&-circle {
				width: 54px;
				height: 54px;
				border-radius: 100%;
				overflow: hidden;
				background: #D3E3FD;
				box-shadow: 1px 2px 4px 0 #3B3B3B40;

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			&-file-name {
				display: flex;
				align-items: center;
				flex: 1;
				font-size: 14px;
				line-height: 18px;
				font-weight: 500;
				color: #3F884F;
			}
		}

		&-toolbar {
			display: flex;
			align-items: center;
			margin-bottom: 12px;

			&-block {
				display: flex;
				align-items: center;
				gap: 8px;
				margin-right: 40px;

				&-rotate {
					display: flex;
					align-items: center;

					&-input {
						font-size: 14px;
						line-height: 18px;
						font-weight: 500;
						margin-top: 2px;
						color: #979797;
						cursor: pointer;
					}
				}
			}

			&-drop {
				font-size: 12px;
				line-height: 16px;
				color: #979797;
				cursor: pointer;

				&:hover {
					color: #BA4C4C;
				}
			}
		}

		&-cropper-area {
			overflow: hidden;
			margin-bottom: 12px;
		}

		&-cropper {
			height: 426px;
			width: 100%;
			background: #D3E3FD;
		}

		&-save-section {
			display: flex;
			align-items: center;
			flex: 1;
			justify-content: flex-end;
		}
	}
}
</style>
